import React, { useState } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import { DollarCircleFilled, EuroCircleFilled, PoundCircleFilled, QrcodeOutlined, ShareAltOutlined, EditOutlined, CopyOutlined, LinkOutlined , LoadingOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { message, List, Button, Tag, Card, Input, InputNumber, Row, Col, Space, Checkbox, Modal, Typography, Switch, Select } from 'antd';
import { Link } from 'react-router-dom';
import Avatar from 'antd/lib/avatar/avatar';
import firebase from 'firebase/app';
import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import ProductSwitcher, { capitalize }  from '../../utils/ProductSwitcher';
import RunningTotalsCard from '../customer/RunningTotalsCard';
import { PaymentsMaxAmounts } from '../../utils/Constants';
import { paymentUrl, shareUrl, shareQRUrl, getAppBaseUrl } from '../../utils/URLBuilder';
import QRImageView from '../customer/QRImageView';
import MoneyTitle from '../customer/MoneyTitle';
import {ccyIsoCodeToSymbolMap} from '../../utils/CurrencyUtils';
import { TagSpecificPricingView } from '../admin/TagSpecificPricingView';

import ClientAvatar from './ClientAvatar';
import { PauseButton } from './AccountProfileComponents';
import { TagImageEditor, TemplateTagImageEditor } from './UploadableImageEditor';
import { useClientDetails } from './useClientDetails';

const { TextArea } = Input;
const { Text } = Typography;
const short = require('short-uuid');

const ccy = ccyIsoCodeToSymbolMap;

const ccy_icons = {
    'EUR': <EuroCircleFilled style={{ fontSize: '20px', color: '#00c' }} />,
    'USD': <DollarCircleFilled style={{ fontSize: '20px', color: '#08c' }} />,
    'GBP': <PoundCircleFilled style={{ fontSize: '20px', color: '#70c' }} />,
};

/*** MAX value for a default selection is 250 €/£/$ */
const MAX_TAG_VALUE = 250;
const MIN_TAG_VALUE = 1;

const cardStyle = {
    width: '100%',
    textAlign: 'start',
    backgroundColor: 'transparent'
};

export function iconForCCY(ccy) {
    let icon = ccy_icons[ccy];
    return (icon) ? icon : <DollarCircleFilled style={{ fontSize: '20px', color: '#08c' }} />;
}


function itemTitle(item) {
    let title = item.displayName ? `${item.displayName}` : "";
    title = title + ((item.profileBio) ? `, ${item.profileBio}` : "");
    return title;
};

const teamTag = (tag) => {
    return tag.isTeamTag ? <Tag color={'#2db7f5'}>{'Team'}</Tag> : null;
}

export const TagsListItemContextCard = ({ type, tag, tagId, locStrings, onEditTag }) => {
    const { paymentDetails, displayName, profileBio } = tag;

    let currency = paymentDetails ? paymentDetails.currency : 'EUR';
    currency = currency || 'EUR';

    const cardStyle = {
        width: '100%',
        textAlign: 'start',
        backgroundColor: !!tag.is_test ? 'transparent' : 'transparent'
    };

    const [isEditVisible, setIsEditVisible] = useState(false);
    const [inputDisplayName, setDisplayName] = useState(displayName || "");
    const [inputProfileBio, setProfileBio] = useState(profileBio || "");

    const [imageUrl, setImageUrl] = useState(tag && tag.avatar ? tag.avatar : null);

    const handleDisplayNameChange = (e) => {
        setDisplayName(e.target.value);
    }

    const handleProfileBioChange = (e) => {
        setProfileBio(e.target.value);
    }

    const saveTagData = async () => {
        message.loading('Saving...');
        setIsEditVisible(false);
        await onEditTag(
            tag, {
            displayName: inputDisplayName,
            profileBio: inputProfileBio,
            modified: true,
        } );
        message.destroy();
        message.success('New tag content is live!', 1.5)
    }

    const buttonStyle = { marginTop: '5px', marginBottom:'5px' };

    const editAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(true) )}><EditOutlined /> Edit Content</Button>);
    const saveAction = (<Button style={buttonStyle} type='primary' onClick={(e) => ( saveTagData() )}>Save</Button>);
    const cancelAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(false) )}>Cancel</Button>);

    const actions = [];
    if(isEditVisible) {
        actions.push(saveAction);
        actions.push(cancelAction);
    }
    if(!isEditVisible) actions.push(editAction);

    const title = () => {
        let title = `Strikepay ${capitalize(tag.product)} Tag`;

        if (tag.metadata && tag.metadata.nickname) {
            title += ` - ${tag.metadata.nickname}`;
        }

        return title;
    }

    return (
        <Card
            size="default"
            type={type}
            hoverable
            style={cardStyle}
            extra={iconForCCY(currency)}
            actions={actions}
            title={title()}
        >
            { !isEditVisible && (
                <Card.Meta
                    avatar={<Avatar src={imageUrl} size='default' />}
                    title={tag.displayName || 'Display Name'}
                    description={tag.profileBio || 'Profile bio'}
                />
            )}

            { isEditVisible && (
                <>
                    <TagImageEditor
                        tagId={tagId}
                        currentUrl={tag.avatar}
                        isLoggedInMemberAdmin={true}
                        onUpdateImage={(url) => setImageUrl(url)}
                    />

                    <Input addonBefore={"Display name"} value={inputDisplayName} onChange={handleDisplayNameChange}/>
                    <p style={{margin: '10px 0 0 0'}}>Profile bio:</p>
                    <TextArea value={inputProfileBio} onChange={handleProfileBioChange}/>
                </>
            )}
        </Card>
    );
}

export const TagsListItemMetadataCard = ({ type, tag, tagId, locStrings, onEditTag }) => {
    const { metadata } = tag;

    const [isEditVisible, setIsEditVisible] = useState(false);
    const [inputNickname, setNickname] = useState(metadata ? (metadata.nickname ? metadata.nickname : "") : "");
    const [inputLocation, setLocation] = useState(metadata ? (metadata.location ? metadata.location : "") : "");
    const [inputNumber, setNumber] = useState(metadata ? (metadata.number ? metadata.number : "") : "");
    const [inputContactDetails, setContactDetails] = useState(metadata ? (metadata.contact ? metadata.contact : "") : "");
    const [inputContactEmail, setContactEmail] = useState(metadata ? (metadata.email ? metadata.email : "") : "");
    // Smart POS: the QR family (stand / sticker / card / fob) is an armable
    // hand-off device — same behavior, different physical form. This screen
    // may only set or REMOVE a QR-family kind. Any other deviceKind
    // (terminal) is provisioned elsewhere and is strictly read-only here.
    const QR_KINDS = ['stand', 'sticker', 'card', 'fob'];
    const canToggleStand = !tag.deviceKind || QR_KINDS.includes(tag.deviceKind);
    const [inputIsStand, setIsStand] = useState(QR_KINDS.includes(tag.deviceKind));
    const [inputQrKind, setQrKind] = useState(
        QR_KINDS.includes(tag.deviceKind) ? tag.deviceKind : 'stand',
    );

    const saveTagData = async () => {
        message.loading('Saving...');
        setIsEditVisible(false);
        await onEditTag(
            tag, {
                // Spread the existing map: sub-map writes must carry every
                // current key forward, never replace the map with a subset.
                metadata: {
                    ...(tag.metadata || {}),
                    nickname: inputNickname,
                    location: inputLocation,
                    number: inputNumber,
                    contact: inputContactDetails,
                    email: inputContactEmail,
                    lastModified: firebase.firestore.Timestamp.fromDate(new Date()),
                },
                ...(canToggleStand ? { deviceKind: inputIsStand ? inputQrKind : null } : {}),
                // Completing setup only applies to tags that HAVE a setup flow;
                // writing false onto others creates a phantom "Change tag setup"
                // link on the payer page.
                ...(tag.setupRequired != null ? { setupRequired: false } : {}),
            }
        );
        message.destroy();
        message.success('New location saved!', 1.5)
    }

    const buttonStyle = { marginTop: '5px', marginBottom:'5px' };

    const editAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(true) )}><EditOutlined /> Edit Location</Button>);
    const saveAction = (<Button style={buttonStyle} type='primary' onClick={(e) => ( saveTagData() )}>Save</Button>);
    const cancelAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(false) )}>Cancel</Button>);

    const actions = [];
    if(isEditVisible) {
        actions.push(saveAction);
        actions.push(cancelAction);
    }
    if(!isEditVisible) actions.push(editAction);

    return (
        <Card
            title="Private Data (Not shown to users)"
            size="default"
            type={type}
            style={cardStyle}
            actions={actions}
            hoverable
        >
            { !isEditVisible && (
                <div>
                    <p>{`Place Name: ` + (tag.metadata ? tag.metadata.nickname : 'N/A')}</p>
                    <p>{`Address: ` + (tag.metadata ? tag.metadata.location : 'N/A')}</p>
                    <p>{`ID: ` + (tag.metadata ? tag.metadata.number : 'N/A')}</p>
                    <p>{`Phone: ` + (tag.metadata ? tag.metadata.contact : 'N/A')}</p>
                    <p>{`Contact Email: ` + (tag.metadata ? tag.metadata.email : 'N/A')}</p>
                    <p>{`Updated: ` + ((tag.metadata && tag.metadata.lastModified) ? moment(tag.metadata.lastModified.toMillis()).format('DD/MM/YYYY, h:mma') : 'N/A')}</p>
                    <p>
                        {`Smart POS: `}
                        {QR_KINDS.includes(tag.deviceKind)
                            && `${tag.deviceKind} (hand-off device — removable below)`}
                        {!tag.deviceKind && 'Not a hand-off device'}
                        {tag.deviceKind && !QR_KINDS.includes(tag.deviceKind)
                            && `${tag.deviceKind} (hand-off device — managed elsewhere)`}
                    </p>
                </div>
            )}

            { isEditVisible && (
                <>
                    <Input placeholder={"Place Name"} addonBefore={"Place Name:"} value={inputNickname} onChange={(e) => setNickname(e.target.value)} />
                    <Input placeholder={"Address"} addonBefore={"Address:"} value={inputLocation} onChange={(e) => setLocation(e.target.value)} />
                    <Input placeholder={"Unit ID"} addonBefore={"Unit ID:"} value={inputNumber} onChange={(e) => setNumber(e.target.value)} />
                    <Input placeholder={"Phone"} addonBefore={"Phone:"} value={inputContactDetails} onChange={(e) => setContactDetails(e.target.value)} />
                    <Input placeholder={"example@org.com"} addonBefore={"Contact Email:"} value={inputContactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                    {canToggleStand && (
                        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <Switch checked={inputIsStand} onChange={setIsStand} />
                            <span>
                                <strong>Smart POS device</strong> — this QR can be handed
                                payments by the Smart POS (shows in the device grid).
                            </span>
                            {inputIsStand && (
                                <Select value={inputQrKind} onChange={setQrKind} style={{ minWidth: '190px' }}>
                                    <Select.Option value="stand">Stand (on a counter)</Select.Option>
                                    <Select.Option value="sticker">Sticker (window/mirror)</Select.Option>
                                    <Select.Option value="card">Card (carried)</Select.Option>
                                    <Select.Option value="fob">Fob (keyring)</Select.Option>
                                </Select>
                            )}
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}

const PersonCard = ({ avatar, displayName }) => {
    return (
        <div className="container">
            <div className="row">
                <div className="col-12 col-md-6 d-flex align-items-center">
                    <img
                        src={avatar}
                        alt="Avatar"
                        className="rounded-circle mr-3"
                        width="50"
                        height="50"
                    />
                    <span className="h5 mb-0">{displayName}</span>
                </div>
            </div>
        </div>
    );
  }


export const TagSelectedClient = ({ tag, tagId, locStrings, onEditTag, type }) => {
    const { clientId } = tag;
    const client = useClientDetails(clientId);

    const [isEditVisible, setIsEditVisible] = useState(false);
    const [inputClientId, setInputClientId] = useState(clientId);

    const saveTagData = async () => {
        message.loading('Saving...');
        setIsEditVisible(false);
        await onEditTag(
            tag, {
                clientId: inputClientId ? inputClientId : null,
            }
        );
        message.destroy();
        message.success('New clientId saved!', 1.5)
    }

    const buttonStyle = { marginTop: '5px', marginBottom:'5px' };

    const editAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(true) )}><EditOutlined /> Edit Client Id</Button>);
    const saveAction = (<Button style={buttonStyle} type='primary' onClick={(e) => ( saveTagData() )}>Save</Button>);
    const cancelAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(false) )}>Cancel</Button>);

    const actions = [];
    if(isEditVisible) {
        actions.push(saveAction);
        actions.push(cancelAction);
    }
    if(!isEditVisible) actions.push(editAction);

    return (
        <Card
            title={`Client Id: ` + (clientId ? clientId : 'No client set')}
            size="default"
            type={type}
            style={cardStyle}
            actions={actions}
            hoverable
        >
            <div className='mb-2'>
                {client &&
                    <PersonCard avatar={client.avatar} displayName={client.displayName} />
                }
            </div>

            { isEditVisible && (
                <>
                    <Input
                        placeholder={"Client Id"}
                        addonBefore={"Client Id:"}
                        value={inputClientId}
                        onChange={(e) => setInputClientId(e.target.value)} />
                </>
            )}
        </Card>
    );
}

export const TemplateTagListItemActionTextCard = ({ type, tag, tagId, locStrings, onEditTag }) => {
    const { actionText } = tag;

    const [isEditVisible, setIsEditVisible] = useState(false);
    const [heading, setHeading] = useState(actionText && actionText.heading);
    const [subheading, setSubheading] = useState(actionText && actionText.subheading);
    const [subsubheading, setSubsubheading] = useState(actionText && actionText.subsubheading);
    const [signupImage, setSignupImage] = useState(actionText && actionText.signupImage);
    const [questionsTitle, setQuestionsTitle] = useState(actionText && actionText.questionsTitle);
    const [questions, setQuestions] = useState(actionText && actionText.questions);

    const addEmptyActionText = async () => {
        message.loading('Saving...');
        setIsEditVisible(false);
        await onEditTag(
            tag, {
                actionText: {
                    questions: [],
                },
                updatedAt: firebase.firestore.Timestamp.fromDate(new Date()),
            }
        );
        message.destroy();
        message.success('Template tag data updated!', 1.5)
    }

    if (!actionText) {
        return (
            <div style={{ width: '100%', textAlign: 'center' }}>
                <Button type='secondary' onClick={addEmptyActionText}>Add Action Text</Button>
            </div>
        );
    }

    const saveTagData = async () => {
        message.loading('Saving...');
        setIsEditVisible(false);
        await onEditTag(
            tag, {
                actionText: {
                    heading: heading ? heading : null,
                    subheading: subheading ? subheading : null,
                    subsubheading: subsubheading ? subsubheading : null,
                    signupImage: signupImage ? signupImage : null,
                    questionsTitle: questionsTitle ? questionsTitle : null,
                    questions: questions ? questions : [],
                },
                updatedAt: firebase.firestore.Timestamp.fromDate(new Date()),
            }
        );
        message.destroy();
        message.success('Template tag data updated!', 1.5)
    }

    const addNewQuestion = () => {
        setQuestions([
            ...questions,
            {
                question: "",
                answer: "",
            }
        ]);
    }

    const deleteQuestion = (index) => {
        let items = [...questions];
        items.splice(index, 1)
        setQuestions(items);
    }

    const updateQuestion = (index, value, isQuestion) => {
        let items = [...questions];
        let item = {...items[index]};

        if (isQuestion) {
            item.question = value;
        } else {
            item.answer = value;
        }

        items[index] = item;
        setQuestions(items);
    }

    const buttonStyle = { marginTop: '5px', marginBottom:'5px' };

    const editAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(true) )}><EditOutlined /> Edit Template Data</Button>);
    const saveAction = (<Button style={buttonStyle} type='primary' onClick={(e) => ( saveTagData() )}>Save</Button>);
    const cancelAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(false) )}>Cancel</Button>);
    
    const actions = [];
    if(isEditVisible) {
        actions.push(saveAction);
        actions.push(cancelAction);
    }
    if(!isEditVisible) actions.push(editAction);

    return (
        <Card
            title="Template Data"
            size="default"
            type={type}
            style={cardStyle}
            actions={actions}
            hoverable
        >
            { !isEditVisible && (
                <div>
                    <img src={signupImage} style={{ width: '100%', paddingBottom: '16px' }} alt={heading} />
                    <p><strong>Heading: </strong>{(heading ? heading : 'N/A')}</p>
                    <p><strong>Subheading: </strong>{(subheading ? subheading : 'N/A')}</p>
                    <p><strong>Subsubheading:</strong>{(subsubheading ? subsubheading : 'N/A')}</p>
                    <p><strong>Questions Heading: </strong>{(questionsTitle ? questionsTitle : 'N/A')}</p>
                    <br />
                    <p><strong>Questions</strong></p>
                    <QuestionsDisplay questions={questions} />
                </div>
            )}

            { isEditVisible && (
                <>
                    <div style={{ marginLeft: "auto", marginRight: 'auto', width: "intrinsic", marginBottom: "10px"}}>
                        <TemplateTagImageEditor 
                            templateId={tagId}
                            currentUrl={signupImage}
                            isLoggedInMemberAdmin={true}
                            onUpdateImage={(url) => setSignupImage(url)}
                        />
                    </div>
                    <Input placeholder={"Heading"} value={heading} onChange={(e) => setHeading(e.target.value)} />
                    <Input placeholder={"Subheading"} value={subheading} onChange={(e) => setSubheading(e.target.value)} />
                    <Input placeholder={"Subsubheading"} value={subsubheading} onChange={(e) => setSubsubheading(e.target.value)} />
                    <Input placeholder={"Questions Heading"} value={questionsTitle} onChange={(e) => setQuestionsTitle(e.target.value)} />
                    <p style={{ paddingTop: '16px' }}><strong>Questions</strong></p>
                    <QuestionsEdit questions={questions} onUpdateQuestion={updateQuestion} onDeleteQuestion={deleteQuestion} />
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => addNewQuestion()}
                        type="secondary"
                        style={{ width: '100%' }}
                    >Add Question</Button>
                </>
            )}
        </Card>
    );
}

const QuestionsDisplay = ({ questions }) => {
    if (questions) {
        return(
            questions.map((question, index) => {
                return <p key={index}><strong>Q: </strong>{question.question} - <strong>A: </strong>{question.answer}</p>
            })
        );
    } else {
        return null;
    }
}

const QuestionsEdit = ({ questions, onUpdateQuestion, onDeleteQuestion }) => {
    if (questions) {
        return(
            questions.map((q, index) => {
                return (
                    <div key={index} style={{ paddingBottom: '4px' }}>
                        <Input placeholder={"question"} value={q.question} onChange={(e) => onUpdateQuestion(index, e.target.value, true)} style={{ width: '44%', marginRight: '1%'}} />
                        <Input placeholder={"answer"} value={q.answer} onChange={(e) => onUpdateQuestion(index, e.target.value, false)} style={{ width: '48%', marginLeft: '1%', marginRight: '1%' }} />
                        <Button
                            icon={<MinusCircleOutlined />}
                            onClick={() => { onDeleteQuestion(index) }}
                            type="secondary"
                            style={{ width: '5%' }}
                        />
                    </div>
                )
            })
        );
    } else {
        return null;
    }
}

export const TagsListItemValuesCard = ({ type, tag, onEditTag }) => {
    const { paymentDetails, values, selectedValueByIndex } = tag;
    let currency = paymentDetails ? paymentDetails.currency : 'EUR';
    currency = currency || 'EUR';

    const percentage = (100 / values.length).toFixed(0);
    const widthPercentage = `${percentage}%`;
    let spanInputFields = (24/values.length).toFixed(0);
    spanInputFields = spanInputFields < 6 ? 6 : spanInputFields;

    const [isEditVisible, setIsEditVisible] = useState(false);
    const [inputValues, setInputValues] = useState(values || []);

    const setValuesAtIndex = (oldValues, value, index) => {
        let newValues = [ ...oldValues ];
        newValues[index] = value * 100;
        setInputValues([...newValues]);
    };

    const saveValuesArray = async () => {
        message.loading('Saving values..');
        setIsEditVisible(false);
        await onEditTag(
            tag, {
            values : [ ...inputValues ]
        } );
        message.destroy();
        message.success('New tag values live!', 1.5)
    }

    const saveSelectedValue = async (index) => {
        message.loading('Saving new default..');
        await onEditTag(
            tag,
            { selectedValueByIndex : index }
        );
        message.destroy();
        message.success('New default value live!', 1.5)
    }


    const cardStyle = {
        width: '100%',
        textAlign: 'start',
    };

    const gridStyle = {
        width: widthPercentage,
        textAlign: 'center',
    };

    const gridStyleSelected = {
        ...gridStyle,
        backgroundColor: '#535353',
    };

    const valueStyle = {
        fontWeight: 'normal',
        fontSize: '14px',
    };

    const valueStyleSelected = {
        ...valueStyle,
        fontWeight: 'bold',
        color: 'white',
    };

    const buttonStyle = { marginTop: '5px', marginBottom:'5px' };

    const editAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(true) )}><EditOutlined /> Edit Values</Button>);
    const saveAction = (<Button style={buttonStyle} type='primary' onClick={(e) => ( saveValuesArray() )}>Save</Button>);
    const cancelAction = (<Button style={buttonStyle} type='text' onClick={(e) => ( setIsEditVisible(false) )}>Cancel</Button>);

    const actions = [];
    if(isEditVisible) {
        actions.push(saveAction);
        actions.push(cancelAction);
    }
    if(!isEditVisible) actions.push(editAction);

    return (
        <Card
            title="Choose default value"
            size="default"
            type={type}
            style={cardStyle}
            actions={actions}
            hoverable
        >
            { !isEditVisible && inputValues.map((value, index) =>
            (
                <Card.Grid
                    key={`grid-${index}`}
                    style={(index === selectedValueByIndex) ? gridStyleSelected : gridStyle}
                    size="small"
                    onClick={(e) => ( saveSelectedValue(index) )}
                >
                    {
                    (!isEditVisible) &&
                    <p style={(index === selectedValueByIndex) ? valueStyleSelected : valueStyle}>{`${ccy[currency] || currency}${(value / 100).toFixed(0)}`}</p>
                    }
                </Card.Grid>
            )
            )}

            { isEditVisible && (
                (<Row  gutter={[0, 0]}>
                    {(inputValues.map(
                        (value, index) =>
                            (<Col key={`col-input-${index}`} span={spanInputFields}>
                                <InputNumber
                                    key={`input-${index}`}
                                    style={{ width: '100%', marginLeft: 'auto', marginRight: 'auto'}}
                                    placeholder={`${ccy[currency] || currency}${(value / 100).toFixed(0)}`}
                                    defaultValue={(value / 100).toFixed(0)}
                                    min={MIN_TAG_VALUE}
                                    max={MAX_TAG_VALUE}
                                    precision={0}
                                    size={"large"}
                                    onChange={(v) => (setValuesAtIndex(inputValues, v, index))}
                                />
                            </Col>)
                        )
                    )}
                </Row>)
            )}


        </Card>

    );
}

export const TagToggleFeesCheckbox = ({ type, tag, onEditTag, locStrings }) => {
    const { paymentDetails } = tag;
    const { shouldAddOnFees } = paymentDetails || {};
    const defaultChecked = shouldAddOnFees == undefined ? true : shouldAddOnFees;
    const label = "Pre-select accept fees option";
    const successFalse = "You will pay fees by default";
    const successTrue = "Fees will be selected by default";

    const [checked, setChecked] = useState(defaultChecked);

    const onChange = async (e) => {
        message.loading('Saving settings...');
        setChecked(e.target.checked);
        await onEditTag(
            tag, {
            paymentDetails : {
                shouldAddOnFees: e.target.checked
            }
        } );
        message.destroy();
        message.success(e.target.checked ? successTrue : successFalse, 1.5)
    }


    return (
        <p style={{ marginBottom: '20px' }}>
            <Checkbox
                checked={checked}
                disabled={false}
                onChange={onChange}
            >
                {label}
            </Checkbox>
        </p>
    );
}



export const TagsListItemDetail = ({ tag, tagId, locStrings, onEditTag, isAdmin }) => {
    const statusColor = {
        'available': 'geekblue',
        'live': 'green',
    };

    const tagStatus = {
        color: !tag.is_test ?  (tag.status ? (statusColor[tag.status] || 'warning') : 'default') : 'red',
        text: !tag.is_test ? capitalize(tag.status || "unknown") : "Test"
    }

    const isTemplateTag = window.location.pathname.includes('admin/issuers');
    const statusText = tag.status ? (<Tag color={tagStatus.color}>{tagStatus.text}</Tag>) : null;
    const templateTagId = tag.templateTagId ? (<Tag color='#ff0098'>{tag.templateTagId}</Tag>) : null;
    const templateTagUid = tag.tagUid ? (<Tag color='geekblue'>{tag.tagUid}</Tag>) : null;
    const title = templateTagId && templateTagUid
                    ? (<Space align='baseline'> {templateTagId} {templateTagUid} </Space>)
                    : (tag.id ? (<Tag>{tag.id}</Tag>) : null);
    const showMetadataEdit = !isTemplateTag;

    const { claimType } = tag;
    const claimTypes = ['thirdPartyAutoClaim', 'autoClaim'];
    const isAutoClaim = claimTypes.includes(claimType);

    return (
        <Card size='default' style={{ textAlign: 'start', backgroundColor: 'transparent' }}>
            {!tag.isTeamTag && !isTemplateTag && <TagsListItemContextCard type={'inner'} tag={tag} tagId={tagId} locStrings={locStrings} onEditTag={onEditTag} />}

            { !tag.isTeamTag && tag.values && tag.values.length > 0 &&
                <TagsListItemValuesCard type={'inner'} tag={tag} locStrings={locStrings} onEditTag={onEditTag}/>
            }
            <p/>
            { !tag.isTeamTag &&
                <TagToggleFeesCheckbox
                    type="inner"
                    tag={tag}
                    onEditTag={onEditTag}
                    locStrings={locStrings}
                    style={{ marginTop: '8px' }}
                />
            }
            { isAdmin && isTemplateTag && isAutoClaim &&
                <TagSelectedClient tag={tag} tagId={tagId} locStrings={locStrings} onEditTag={onEditTag} type={'inner'} />
            }
            { showMetadataEdit &&
                <TagsListItemMetadataCard type={'inner'} tag={tag} tagId={tagId} locStrings={locStrings} onEditTag={onEditTag} />
            }
            { isTemplateTag &&
                <TemplateTagListItemActionTextCard type={'inner'} tag={tag} tagId={tagId} locStrings={locStrings} onEditTag={onEditTag} />
            }
            { isAdmin && tag.paymentDetails && tag.paymentDetails.currency && tag.product &&
                <TagSpecificPricingView pricing={tag.tagSpecificPricing} currency={tag.paymentDetails.currency} product={tag.product} />
            }
            <Space align='baseline' style={{ marginTop: '5px'}}>
                {statusText}
                {title}
            </Space>

        </Card>
    );
};

export const TagsListItemCompact = ({ tag, tagId, locStrings, isAdmin, doNotShowProfile }) => {
    const statusColor = {
        'available': 'geekblue',
        'live': 'green',
    };

    const tagStatus = {
        color: !tag.is_test ?  (tag.status ? (statusColor[tag.status] || 'warning') : 'default') : 'red',
        text: !tag.is_test ? capitalize(tag.status || "unknown") : "Test"
    }

    const statusText = tag.status ? (<Tag color={tagStatus.color}>{tagStatus.text}</Tag>) : null;
    const useTagUid = tag.tagUid ? (<Tag color='black'>{tag.tagUid}</Tag>) : null;
    const templateTagUid = useTagUid ? useTagUid : (<Tag color='black'>{tag.id}</Tag>);
    const templateTagId = (tag.templateTagId) ? (<Tag color='#ff0098'>{tag.templateTagId}</Tag>) : null;

    const { paymentDetails } = tag;

    let currency = paymentDetails ? paymentDetails.currency : 'EUR';
    currency = currency || 'EUR';

    const getTopLogo = (tag) => {
        if (!tag.isTeamTag) {
            const { actionText } = tag;
            if (actionText && actionText.signupImage) {
                return actionText.signupImage;
            }
            return tag.topLogo;//Support for existing topLogo which is 'not' of a team
        }
        return '';
    }

    const cardStyle = {
        width: '100%',
        textAlign: 'start',
        backgroundColor: 'transparent'
    };

    const topLogoStyle = {
        width: getTopLogo(tag) ? '70%' : '0%',
        marginLeft: 'auto', marginRight: 'auto',
        maxHeight: '150px',
        objectFit: 'contain',
    };

    const cardTitle = () => {
        let title = `${capitalize(tag.product)} Page`;

        if (tag.metadata && tag.metadata.nickname) {
            title += ` - ${tag.metadata.nickname}`;
        }

        return title;
    }

    const getDisplayName = (tag) => {
        if(tag.isTeamTag){
            return (tag.teamName ? tag.teamName : 'Team Page');
        }
        return (tag.displayName ? tag.displayName : 'Display Name');
    }

    const getActionTextTitle = (tag) => {
        const { actionText } = tag;
        const { heading } = actionText || {};
        return heading || 'No heading';
    }

    const getActionTextSubTitle = (tag) => {
        const { actionText } = tag;
        const { subheading } = actionText || {};
        return subheading || 'No subheading';
    }

    const getAvatar = (tag) => {
        if (tag.isTeamTag && tag.topLogo) {
            return tag.topLogo;
        }
        return tag.avatar;
    }

    const getProfileBio = (tag) => {
        if(tag.isTeamTag){
            return (tag.teamDescription ? tag.teamDescription : 'Team Description');
        }
        return (tag.profileBio ? tag.profileBio : 'Profile bio');
    }

    const getLocationData = (tag) => {
        if (tag.metadata) {
            return `Place Name: ${tag.metadata.nickname}\n` +
            `Address: ${tag.metadata.location}\n` +
            `ID: ${tag.metadata.number}\n` +
            `Phone: ${tag.metadata.contact}`;
        }
    }

    return (
        <Card
            size="default"
            type={'inner'}
            style={cardStyle}
            cover={<img style={topLogoStyle} src={getTopLogo(tag)} />}
            extra={<Row>{teamTag(tag)}{iconForCCY(currency)}</Row>}
            title={<Space>{statusText}{cardTitle()}</Space>}
        >
            { !doNotShowProfile &&
            <Row>
                <Col xs={{ span: 24 }} md={{span: tag.metadata ? 14 : 24}}>
                    <Card.Meta
                        avatar={<Avatar src={getAvatar(tag)} size='default' />}
                        title={getDisplayName(tag)}
                        description={getProfileBio(tag)}
                    />
                </Col>
                <Col xs={{ span: 24 }} md={{span: tag.metadata ? 10 : 0}}>
                    <Card.Meta
                        style={{ whiteSpace: 'pre', paddingTop: '16px'}}
                        description={getLocationData(tag)}
                    />
                    
                </Col>
            </Row>
            }
            { doNotShowProfile &&
                <Row>
                    <Col xs={{ span: 24 }} md={{span: tag.metadata ? 14 : 24}}>
                        <Card.Meta
                            title={getActionTextTitle(tag)}
                            description={getActionTextSubTitle(tag)}
                        />
                    </Col>
                </Row>
            }
            <Space direction='vertical' size='small' style={{ marginTop: '10px' }}>
                <Space align='baseline'>
                        {templateTagId}
                        {templateTagUid}
                </Space>
                { tag && tag.totalNetValue &&
                <Space align='baseline'>
                    <RunningTotalsCard type={'inner'} object={tag} asTag />
                </Space>
                }
            </Space>
        </Card>
    );
}

// TODO: Pass Action Components in to the List to control what can be done at the screen level
export const StrikeTagsList = ({ condition, locStrings, client, data, handler, isNoEdit, onEditTag, isAdmin, isTagTemplateListing, currentUser, ...rest }) => {
    const urlBase = getAppBaseUrl();
    const uuids = {};

    const defaultAmount = null;

    const [amount, setAmount] = useState(defaultAmount);
    const [name, setName] = useState(null);
    const [reference, setReference] = useState(null);
    const [link, setLink] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [tagId, setTagId] = useState(null);
    const [tag, setTag] = useState(null);
    const [maxAmount, setMaxAmount] = useState(500000);
    const [error, setError] = useState(false);

    const uuid = () => {
        const uuid = short.generate();
        return uuid;
    }

    const shareLink = (tag, queryParam) => {
        const iUUID = uuids[tag.id] || uuid();
        uuids[tag.id] = iUUID;
        return `tags/${tag.id}?${queryParam}=${iUUID}`;
    }

    const fullURIForShare = (tag, queryParam) => {
        const iUUID = uuids[tag.id] || uuid();
        if(queryParam === 'qr') {
            return shareQRUrl(tag, null)
        } else {
            return shareUrl(tag, null)
        }

        // if(!!isAdmin) {
        //     const uri = shareLink(tag, queryParam);
        //     return `${urlBase}/${uri}`;
        // } else {
        //     return (tag.templateTagId && tag.tagUid)
        //     ? `${urlBase}/tags/${tag.templateTagId}?uid=${tag.tagUid}`
        //     : `${urlBase}/tags/${tag.id}`;
        // }
    };

    const fullURLForQR = (tag) => {
        return shareQRUrl(tag, null)
    };

    const duplicateTemplateTag = async (templateTag) => {
        const duplicateId = window.prompt("Please enter the name/id to give the duplicated template tag");

        if (!duplicateId) {
            window.alert("Error: Please enter a name/id to duplicate the template tag");
            return;
        }

        const db = firebase.firestore();

        const issuerId = "strike";
        const templateTagDoc = await db.collection('issuers').doc(issuerId).collection("template_tags").doc(duplicateId).get();

        if (templateTagDoc.exists) {
          window.alert("Error: This template tag name/id already exists. Please try again with a different name/id");
          return;
        }

        if (templateTag.displayName) {
            templateTag.displayName = `Duplicate of ${templateTag.displayName}`;
        } else {
            templateTag.displayName = `Duplicate of ${templateTag.id}`;
        }

        // Template tag obj from client has added id, we want to remove before saving
        delete templateTag.id;

        await db.collection('issuers').doc(issuerId).collection("template_tags").doc(duplicateId)
            .set({
                ...templateTag,
                clientId: null,         // Remove clientId from template tag duplication (only for auto claim)
                modified: false,
                createdAt: firebase.firestore.Timestamp.now(),
                updatedAt: firebase.firestore.Timestamp.now(),
            });
    }

    const createPaymentLink = async (tag) => {
        setAmount(defaultAmount);
        setName(null);
        setReference(null);

        setTag(tag);
        setTagId(tag.id);
        setMaxAmount(PaymentsMaxAmounts.values[tag.paymentDetails.currency]);

        setModalVisible(true);
    }

    const handleAmountChange = (value) => {
        if ((value * 100) > maxAmount) {
            setError(true)
        } else {
            setError(false);
            setAmount(value);
        }
    }

    const handleNameChange = (e) => {
        setName(e.target.value);
    }

    const handleReferenceChange = (e) => {
        setReference(e.target.value);
    }

    const handleCancel = () => {
        setModalVisible(false);
    };

    const createLink = () => {
        const safeAmount = amount ? (amount * 100).toFixed(0) : null;
        const linkUrl = paymentUrl(tagId, safeAmount, name, reference)
        setLink(linkUrl);
    }

    const onClickShare = async (tag, queryParam) => {
        const displayName = tag.displayName;
        const placeOfWork = tag.placeOfWork || 'Strike';
        const product = tag.product;

        const productSwitcher = new ProductSwitcher({ locStrings });
        const productMap = productSwitcher.getProductMap(product);

        const shareText = `${productMap.action} ${displayName} with ${placeOfWork}`;

        const shareData = {
            title: displayName,
            text: shareText,
            url: `${fullURIForShare(tag, queryParam)}`,
        }

        try {
            await navigator.share(shareData)
        } catch (err) {
            window.open(shareData.url);
        }
    }

    const toggleLivePauseForTag = async (tag) => {
        let oldStatus = tag.status;
        let newStatus = tag.status;

        if (oldStatus === 'live') {
            newStatus = 'paused';
        } else if (oldStatus === 'paused') {
            newStatus = 'live';
        }

        if (oldStatus !== newStatus) {
            try {
                let tagRecentStatusHistory = tag.recentStatusHistory ? tag.recentStatusHistory : [];

                tagRecentStatusHistory.unshift({
                    previousStatus: oldStatus,
                    newStatus: newStatus,
                    remarks: 'Admin paused/enabled tag',
                    createdAt: firebase.firestore.Timestamp.now(),
                    updatedBy: {
                        uid: (currentUser && currentUser.uid)? currentUser.uid : null,
                        displayName: (currentUser && currentUser.displayName)? currentUser.displayName : null,
                        email: (currentUser && currentUser.email)? currentUser.email : null,
                    }
                });

                if (tagRecentStatusHistory.length > 5) {
                    tagRecentStatusHistory.pop();
                }

                const db = firebase.firestore();
                await db.collection('tags').doc(tag.id).update({
                    status: newStatus,
                    recentStatusHistory: tagRecentStatusHistory,
                    updatedAt: firebase.firestore.Timestamp.now()
                });
            } catch (error) {
                console.log('error:' + error.message);
            }
        }
    }

    const buttonStyle = { marginLeft: 'auto', marginRight: 'auto', width: `100%`, marginBottom: '10px' };

    const getActions = (item) => {
        let editButtonAdded = [];

        if (!isNoEdit || isAdmin) {
            editButtonAdded = [
                <Link to={{ pathname: `${rest.match.url}/${item.id}`, state: { fromTagsList: true } }}>
                    <Button
                        icon={<EditOutlined />}
                        type="secondary"
                        style={buttonStyle}
                    >Edit</Button>
                </Link>,
            ];
        }

        const actions = [
            ...editButtonAdded,
            <Button
                icon={<QrcodeOutlined />}
                href={!!isAdmin ? null : fullURLForQR(item)}
                onClick={!!isAdmin ? ((e)=> (onClickShare(item, 'qr'))) : null}
                type="secondary"
                style={buttonStyle}
            >QR Code</Button>,
            <Button
                icon={<ShareAltOutlined />}
                onClick={((e)=> (onClickShare(item, 'uid')))}
                type="primary"
                style={buttonStyle}
            >Share</Button>
        ];

        if (true) {
            actions.push(
                <Button
                    icon={<LinkOutlined />}
                    onClick={((e)=> (createPaymentLink(item)))}
                    type="secondary"
                    style={buttonStyle}
                >Create Link</Button>,
            );
        }

        if (isAdmin && isTagTemplateListing) {
            actions.push(
                <Button
                    icon={<CopyOutlined />}
                    onClick={((e)=> (duplicateTemplateTag(item)))}
                    type="secondary"
                    style={buttonStyle}
                >Duplicate</Button>,
            );
        }

        if (isAdmin) {
            actions.push(
                <PauseButton
                    isRestricted={item.status === 'paused'}
                    pauseHandler={() => toggleLivePauseForTag(item)}
                />
            );
        }

        return actions;
    }

    const copySuccess = () => {
        message.success('Link copied', 5);
    };

    const styleModalInputs = {
        marginBottom: "10px",
    };

    return (
        <>
            <List
                style={{ textAlign: 'start', maxWidth: '800px' }}
                itemLayout="vertical"
                size='large'
                className="centred-action-buttons"
                pagination
                loading={!condition()}
                dataSource={data}
                renderItem={item => (
                    <List.Item
                        key={item.id}
                        actions={getActions(item)}
                    >
                        <TagsListItemCompact
                            key={`list-${item.id}`} tag={item} tagId={item.id} locStrings={locStrings} onEditTag={onEditTag} isAdmin={isAdmin}
                            doNotShowProfile={isTagTemplateListing}
                        />
                    </List.Item>
                )}
            />

            <Modal
                visible={modalVisible}
                title="Create link"
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Close
                    </Button>,
                ]}
            >
                <div>
                    <p>Fill in any combination of the below to generate a link that will pre-fill them for the recipient. These fields will not be editable by the receiver. <br/><br/> Links are multi-use and are safe to try out before distributing.</p>
                </div>

                <div style={styleModalInputs}>
                    <span>Name:</span>
                    <Input
                        value={name}
                        placeholder={"John O'Neill"}
                        onChange={handleNameChange}
                    />
                </div>

                <div style={styleModalInputs}>
                    <span>Reference:</span>
                    <Input
                        value={reference}
                        placeholder={"Y302JDFLWI"}
                        onChange={handleReferenceChange}
                    />
                </div>

                <div style={styleModalInputs}>
                    <span>Amount:</span>
                    <br/> {/* Breaking here due to InputNumber having a different behaviour than the normal inputs */}
                    <InputNumber
                        max={maxAmount}
                        defaultValue={amount}
                        precision={2}
                        controls={true}
                        value={amount}
                        onChange={handleAmountChange}
                    />

                    { error && (
                        <p style={{ color: 'red' }}>{`Error: Exceeded max value of`} <MoneyTitle amount={maxAmount} currency={tag.paymentDetails.currency} digits={2} />{` - please enter a lower amount.`}</p>
                    )}

                </div>

                <Button key="createLink" onClick={createLink}>
                    Generate link
                </Button>

                <div style={{ textAlign: "center", marginTop: "20px", display: link ? "block" : "none" }}>
                    <div className="copyableBlock">
                        <span>Tap to copy link</span>

                        <CopyToClipboard text={link}
                            onCopy={() => copySuccess() }>
                            <button className="promoCopyable">Copy link</button>
                        </CopyToClipboard>
                    </div>
                    <div>
                    { link &&
                        <div className="hasNestedComps elQRImageView">
                            <div className="elSquareimage">
                                <QRImageView url={link} tag={tag} tagId={tagId} />
                            </div>
                        </div>
                    }
                    </div>
                </div>
            </Modal>
        </>
    );

};




